/**
 * DockDrawer — "Edit dock settings" / "Add dock" centered modal (May 2026 Figma).
 *
 * Reuses the shared .os-* modal shell + .bc-* form/network styles already in
 * pages.css (do not modify those — they are owned by CameraDrawer / Body
 * Cameras).
 *
 * Tabs:
 *   ⚙  General           → device name, serial number, site, checkout association
 *   📶 Network settings  → DNS server, DHCP SERVER toggle + IP / Subnet / Gateway
 *
 * Props:
 *   open       : boolean
 *   dock       : existing dock device (null when adding)
 *   onClose    : () => void
 *   onSaved    : () => void
 */
import { useEffect, useState } from 'react';
import dockService from '../../services/dockService';
import checkoutService from '../../services/checkoutService';
import { useTenant } from '../../context/TenantContext';

const EMPTY = {
  deviceName: '',
  serialNumber: '',
  siteId: '',
  checkoutId: '',
  // Network
  dnsServer: '',
  dhcpServer: true,
  ipAddress: '',
  subnetMask: '',
  subnetGateway: '',
};

function formFromDock(d) {
  if (!d) return EMPTY;
  const net = d.networkSettings || d.network || {};
  return {
    deviceName:   d.dockName     || d.deviceName || '',
    serialNumber: d.serialNumber || '',
    siteId:       d.siteId != null ? String(d.siteId) : '',
    checkoutId:   d.checkoutId != null ? String(d.checkoutId) : '',
    dnsServer:    net.dnsServer    || '',
    dhcpServer:   net.dhcpServer ?? true,
    ipAddress:    net.ipAddress    || d.ipAddress || '',
    subnetMask:   net.subnetMask   || '',
    subnetGateway: net.subnetGateway || '',
  };
}

export default function DockDrawer({ open, dock, onClose, onSaved }) {
  const { orgId, sites } = useTenant();
  const isEdit = !!dock?.dockId;
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [checkouts, setCheckouts] = useState([]);
  const [checkoutsLoading, setCheckoutsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(formFromDock(dock));
      setTab('general');
      setError(null);
      setSubmitting(false);
      setCheckouts([]);
    }
  }, [open, dock]);

  // Reload checkouts whenever the selected site changes
  useEffect(() => {
    if (!open || !orgId || !form.siteId) {
      setCheckouts([]);
      return;
    }
    let cancelled = false;
    setCheckoutsLoading(true);
    checkoutService
      .listCheckouts(orgId, { siteId: Number(form.siteId), limit: 200 })
      .then((data) => {
        if (!cancelled) setCheckouts(data?.content ?? data ?? []);
      })
      .catch(() => { if (!cancelled) setCheckouts([]); })
      .finally(() => { if (!cancelled) setCheckoutsLoading(false); });
    return () => { cancelled = true; };
  }, [open, orgId, form.siteId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set    = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const toggle = (key) => ()  => setForm((p) => ({ ...p, [key]: !p[key] }));

  const canSubmit =
    form.deviceName.trim().length > 0 &&
    form.serialNumber.trim().length > 0 &&
    !!form.siteId &&
    !!orgId &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        dockName:     form.deviceName.trim(),
        serialNumber: form.serialNumber.trim(),
        siteId:       Number(form.siteId),
        checkoutId:   form.checkoutId ? Number(form.checkoutId) : null,
        networkSettings: {
          dnsServer:     form.dnsServer.trim()     || null,
          dhcpServer:    form.dhcpServer,
          ipAddress:     form.ipAddress.trim()     || null,
          subnetMask:    form.subnetMask.trim()    || null,
          subnetGateway: form.subnetGateway.trim() || null,
        },
      };
      if (isEdit) {
        await dockService.patchDock(orgId, dock.dockId, payload);
      } else {
        await dockService.createDock(orgId, payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? err.message
        ?? `Failed to ${isEdit ? 'update' : 'add'} dock`,
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit dock settings' : 'Add dock'}
      onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose?.()}
    >
      <div className="os-modal bc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit dock settings' : 'Add dock'}</h2>
        </header>

        <div className="os-modal-body">
          <aside className="os-side">
            <button
              type="button"
              className={`os-side-item ${tab === 'general' ? 'active' : ''}`}
              onClick={() => setTab('general')}
            >
              <i className="fas fa-cog" />
              <span>General</span>
            </button>
            <button
              type="button"
              className={`os-side-item ${tab === 'network' ? 'active' : ''}`}
              onClick={() => setTab('network')}
            >
              <i className="fas fa-wifi" />
              <span>Network settings</span>
            </button>
          </aside>

          <section className="os-pane bc-pane">
            {tab === 'general' && (
              <div className="bc-form">
                <div className="bc-field">
                  <label className="os-label">Device name<span className="os-req">*</span></label>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="Dock-1"
                    value={form.deviceName}
                    onChange={set('deviceName')}
                  />
                </div>

                <div className="bc-field">
                  <label className="os-label">Serial number<span className="os-req">*</span></label>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="01-23-11-10"
                    value={form.serialNumber}
                    onChange={set('serialNumber')}
                  />
                </div>

                <div className="bc-field">
                  <label className="os-label">
                    Site<span className="os-req">*</span>
                    <i className="fas fa-question-circle bc-help" title="Site this dock belongs to" />
                  </label>
                  <select
                    className="os-input os-select"
                    value={form.siteId}
                    onChange={set('siteId')}
                  >
                    <option value="">Select site</option>
                    {(sites || []).map((s) => (
                      <option key={s.siteId ?? s.id} value={s.siteId ?? s.id}>
                        {s.siteName ?? s.name ?? `Site ${s.siteId ?? s.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bc-field">
                  <label className="os-label">
                    Checkout association
                    <i className="fas fa-question-circle bc-help" title="Kiosk (checkout station) this dock belongs to" />
                  </label>
                  <select
                    className="os-input os-select"
                    value={form.checkoutId}
                    onChange={set('checkoutId')}
                    disabled={!form.siteId || checkoutsLoading}
                  >
                    <option value="">
                      {!form.siteId
                        ? 'Select a site first'
                        : checkoutsLoading
                          ? 'Loading…'
                          : checkouts.length === 0
                            ? 'No checkouts in this site'
                            : 'None'}
                    </option>
                    {checkouts.map((co) => (
                      <option key={co.checkoutId} value={co.checkoutId}>
                        {co.checkoutName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {tab === 'network' && (
              <div className="bc-form">
                <div className="bc-field">
                  <label className="os-label">
                    DNS server
                    <i className="fas fa-question-circle bc-help" title="DNS server address" />
                  </label>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="192.0.2.1"
                    value={form.dnsServer}
                    onChange={set('dnsServer')}
                  />
                </div>

                <div className="bc-net-section">
                  <div className="bc-net-section-header">
                    <span className="bc-net-section-title">DHCP SERVER</span>
                    <label className="tg-toggle-switch">
                      <input
                        type="checkbox"
                        checked={form.dhcpServer}
                        onChange={toggle('dhcpServer')}
                      />
                      <span className="tg-toggle-slider" />
                    </label>
                  </div>

                  {form.dhcpServer && (
                    <div className="bc-net-fields">
                      <div className="bc-field">
                        <label className="os-label">
                          IP address<span className="os-req">*</span>
                          <i className="fas fa-question-circle bc-help" title="IPv4 address served by DHCP" />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.ipAddress}
                          onChange={set('ipAddress')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Subnet mask<span className="os-req">*</span>
                          <i className="fas fa-question-circle bc-help" title="Subnet mask served by DHCP" />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.subnetMask}
                          onChange={set('subnetMask')}
                        />
                      </div>
                      <div className="bc-field">
                        <label className="os-label">
                          Subnet gateway
                          <i className="fas fa-question-circle bc-help" title="Default gateway served by DHCP" />
                        </label>
                        <input
                          type="text"
                          className="os-input"
                          placeholder="123.123.123.123"
                          value={form.subnetGateway}
                          onChange={set('subnetGateway')}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        {error && <div className="os-modal-error">{error}</div>}

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

